import React, { useEffect, useState } from 'react';
import { Controller, FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import Editor from 'react-simple-wysiwyg';
import { FilePickerChange, FormField, MultiFilePicker } from '@tag/tag-components-react-v4';

import { BaseControlProps } from '../../models/BaseControlProps';
import { UserAPIService } from '../../services/UserAPIService';
import { useMessage } from '../../components/Common/MessageContext/MessageContext';
import { UtilService } from '../../services/UtilService';
import { UserGetRequest } from '../../models/UserGetRequest';
import { useAuth } from '../../services/AuthContext';
import { User } from '../../models/User';
import { MessageAttachmentPutRequest } from '../../models/MessageAttachmentPutRequest';
import { UserOption } from '../../models/UserOption';
import { MessageAPIService } from '../../services/MessageAPIService';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Checkbox, Autocomplete, Grid, IconButton, Select, SelectChangeEvent, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PensionScheme } from '../../models/PensionScheme';
import { PayGroup } from '../../models/PayGroup';
 
export interface SendMessageProps extends BaseControlProps {
    visible: boolean
    setVisible: (visible: boolean) => void;
}

const SendMessage = (props: SendMessageProps) => {
    const pageSize = 30;
    const maxSizeInMB = 5;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    const userApiService = new UserAPIService();
    const messageApiService = new MessageAPIService();
    const { showMessage } = useMessage();
    const [scopeGet, applyGet] = useState<UserGetRequest>({
        userId: 0,
        pageSize: pageSize,
        pageNo: 0,
        filterSearchString: "",
        orderByString: ""
    });
    const authContext = useAuth();
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [pensioners, setPensioners] = useState<User[]>([]);
    const [inpSelectAllDisabled, setInpSelectAllDisabled] = useState<boolean>(false);
    const [inpPensionersDisabled, setInpPensionersDisabled] = useState<boolean>(false);
    const [inpPaygroupDisabled, setInpPaygroupDisabled] = useState<boolean>(false);
    const [inpSchemeDisabled, setInpSchemeDisabled] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [inputPensioner, setInputPensioner] = useState('');
    const [pensionSchemeList, setPensionSchemeList] = useState<PensionScheme[]>([]);
    const [payGroupList, setPayGroupList] = useState<PayGroup[]>([]);
    const {
        handleSubmit,
        formState: { isSubmitting, isSubmitted, isSubmitSuccessful, errors },
        control,
        reset,
      } = useForm();
    const [formLoading, setFormLoading] = useState<boolean>(false);

    const getAllPensioners = () => {
        setLoading(true);
        scopeGet.filterSearchString = getSeachFilters();
        scopeGet.orderByString = getOrderByFilters();
        applyGet(scopeGet);
        userApiService.getAllPensioners(scopeGet, authContext.token).then((response) => {
            setLoading(false);
            if (response.error) {
                if (props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }
                showMessage(response.error, 'error');
            }
            else {
                setPensioners(response.response?.users ?? []);
            }
        });
    }

    const getOrderByFilters = () => {
        return  `{
            "OrderByFields": [
              {
                "FieldName": "createdOn",
                "OrderDirection": "DESC"
              }
            ]
        }`
    }

    const getSeachFilters = () => {
        let searchFilters = [];

        if (inputPensioner.trim() !== "") {
            searchFilters.push(`{"FieldName":"emailAddress","SearchType":"startswith","SearchValue":"${inputPensioner}"}`);
        }

        return searchFilters.length >= 0 ? `{"SearchFilters": [${searchFilters.join(",")}]}` : '';
    }

    
    const getPensionSchemeAndPayGroup = () => {
        userApiService.getPensionSchemeAndPayGroup({ userId: 0 }, authContext.token).then((response) => {
            if (response.error) {
                if (props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }
                showMessage(response.error, 'error');
            }
            else {

                if (response.response?.pensionSchemes && response.response?.pensionSchemes.length  > 0) {
                    setPensionSchemeList(response.response?.pensionSchemes);
                }

                if (response.response?.payGroups && response.response?.payGroups.length  > 0) {
                    setPayGroupList(response.response?.payGroups);
                }
            }
        })
    }

    
    useEffect(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeoutId = setTimeout(() => {
            const getAdminUsersAsync = async () => {
                await getAllPensioners();
            }

            getAdminUsersAsync();
        }, 900);

        setSearchTimeout(timeoutId);

        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };

    }, [inputPensioner]);

    useEffect(() => {
        if (props.visible) {
            getAllPensioners();

            getPensionSchemeAndPayGroup();
        }
    }, [props.visible]);

    const handleClose = () => {
        props.setVisible(false);
        setInpSelectAllDisabled(false);
        setInpPensionersDisabled(false);
        setInpPaygroupDisabled(false);
        setInpSchemeDisabled(false);
        reset();
    }

    const transformPensioners = (users: User[]): UserOption[] => {
        return users.map(user => ({
            label: user.emailAddress + " - (" + user.peopleXDId +")",
            value: user.userId.toLocaleString()
        }));
    }

    const transformPaygroups = (paygroups: PayGroup[]): UserOption[] => {
        return paygroups.map(pay => ({
            label: pay.payGroupCode,
            value: pay.payGroupCode
        }));
    }

    const transformSchemes = (schemes: PensionScheme[]): UserOption[] => {
        return schemes.map(scheme => ({
            label: scheme.pensionSchemeName,
            value: scheme.pensionSchemeCode
        }));
    }

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                if (reader.result) {
                    resolve(reader.result as string);
                } else {
                    reject(new Error("File reading error"));
                }
            }
            reader.onerror = error => reject(error);
        })
    }

    const submitMessage: SubmitHandler<FieldValues> = async ({ fail, message, sendToAll, pensionersList, paygroupList, schemeList, attachments }) => {
        setFormLoading(true);
        const documents: MessageAttachmentPutRequest[] = [];
        const attachmentIds: number[] = [];

        if (attachments) {
            for (const file of attachments) {
                if (file.size <= maxSizeInBytes) {
                    const base64String = await fileToBase64(file);
                    const base64Document = base64String.split(',')[1];
                    const fileName = file.name;
                    const fileExtension = fileName.split('.').pop() || '';
        
                    documents.push({
                        documentEncoded: base64Document,
                        fileName: fileName,
                        fileExtension: fileExtension
                    })
                }
            }
        }

        try {
            await Promise.all(documents.map(document => 
                new Promise((resolve, reject) => 
                    messageApiService.putDocumentAttachment(document, authContext.token)
                        .then(function (response) {
                            if (response.error) {
                                reject(response.error);
                                showMessage(response.error, 'error');
                            } else {
                                if (response.response?.portalMessageAttachmentId) {
                                    attachmentIds.push(response.response?.portalMessageAttachmentId);
                                }
                                resolve("Uploaded attachment");
                            }
                        })
                        .catch(function (error) {
                            reject('Error uploading attachment '+error);
                            showMessage("Error uploading file : "+error, 'error');
                        })
                )
            ));

            return new Promise((resolve, reject) => {
                const userIds = pensionersList ? pensionersList.map((pensioner: any) => pensioner.value).join(', ') : '';
                const schemeCodes = schemeList ? schemeList.map((scheme: any) => scheme.value).join(', ') : '';
                const paygroupcodes = paygroupList ? paygroupList.map((paygroup: any) => paygroup.value).join(', ') : '';
                messageApiService.sendMessage({
                        message: message,
                        sendToUserIds: userIds,
                        sendToAll: (userIds == '' && schemeCodes == '' && paygroupcodes == '') ? 'true' : sendToAll,
                        sendToSchema: schemeCodes,
                        sendToPayGroup: paygroupcodes,
                        attachmentIds: attachmentIds.join(', ')
                    }, authContext.token)
                    .then(function (response) {
                        setFormLoading(false);
                        if (response.error) {
                            reject(response.error);
                            showMessage(response.error, 'error');
                        } else {
                            resolve("Successfully created message");
                            showMessage("Message created successfully", 'success');
                            handleClose();
                        }
                    })
                    .catch(function (error) {
                        reject('Error sending message '+error);
                        showMessage("Error creating message : "+error, 'error');
                    })
            });
            
        } catch (error) {
            throw error;
        }
    }

    return(
        <>
            <Dialog
                open={props.visible}
                onSubmit={handleSubmit(submitMessage)}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: { p: { xs: 2, sm: 3 }, m: { xs: 1, sm: 2 }, width: '100%' },
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.2rem' }}>
                    Send Message to Pensioners
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <form onSubmit={handleSubmit(submitMessage)}>

                        {/* Message Textarea */}
                        <Controller
                            render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                                <TextField
                                    error={!!error}
                                    helperText={error ? error.message : ''}
                                    fullWidth
                                    InputProps={{
                                        inputComponent: Editor,
                                        inputProps: {
                                            name,
                                            disabled: formLoading,
                                            value: value,
                                            onChange: onChange,
                                            containerProps: { style: { minHeight: '200px', width: '100%' } }
                                        }
                                    }}
                                    sx={{ mb: 2}}
                                />
                            )}
                            control={control}
                            name="message"
                            rules={{ required: 'Please enter a message' }}
                        />

                        {/* Message Attachment FilePicker */}
                        <Controller 
                            render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                                <FormField 
                                    editor={
                                        <MultiFilePicker 
                                            name={name} 
                                            onChange={(file: FilePickerChange) => {
                                                const invalidFiles: string[] = [];
                                                // Loop through the selected files
                                                file.value?.forEach((f) => {
                                                    if (f.size > maxSizeInBytes) {
                                                        invalidFiles.push(f.name); // Track invalid file names
                                                    }
                                                });

                                                // Show alert for invalid files
                                                if (invalidFiles.length > 0) {
                                                    showMessage(`The following files exceed the ${maxSizeInMB}MB limit: ${invalidFiles.join(', ')}`, 'error');
                                                }
                                                onChange(file);
                                            }} 
                                            disabled={formLoading} 
                                            accept='.doc,.docx,.pdf,image/*'
                                            uploading={isSubmitting}
                                            inputProps={{ style: { marginTop: 2, width: '100%' }}}
                                        />
                                    }
                                />
                            )}
                            control={control}
                            name="attachments"
                        />

                        <Grid container spacing={2} alignItems="center">
                            {/* Send to all pensioners checkbox */}
                            <Grid item xs={12}>
                                <Controller
                                    render={({ field: { onChange, value, name }, fieldState: { error }}) => (
                                        <FormControlLabel
                                            label="Send to All"
                                            control={
                                                <Checkbox 
                                                    checked={value}
                                                    name={name} 
                                                    onChange={(e) => {
                                                        onChange(e.target.checked);
                                                        if (e.target.checked) {
                                                            setInpPensionersDisabled(true);
                                                            setInpPaygroupDisabled(true);
                                                            setInpSchemeDisabled(true);
                                                        } else {
                                                            setInpPensionersDisabled(false);
                                                            setInpPaygroupDisabled(false);
                                                            setInpSchemeDisabled(false)
                                                        }
                                                    }} 
                                                    disabled={formLoading || inpSelectAllDisabled} 
                                                />
                                            }
                                        />
                                    )}
                                    control={control}
                                    name="sendToAll"
                                />
                            </Grid>

                            {/* Pensioners list*/}
                            <Grid item xs={12}>
                                <Controller 
                                    render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                                        
                                        <Autocomplete
                                            multiple
                                            id="pensioners-checkboxes"
                                            limitTags={2}
                                            disabled={inpPensionersDisabled || formLoading}
                                            options={transformPensioners(pensioners)}
                                            disableCloseOnSelect
                                            getOptionLabel={(option) => option.label}
                                            filterSelectedOptions
                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                            onInputChange={(e, newInputValue) => setInputPensioner(newInputValue)}
                                            inputValue={inputPensioner}
                                            loading={loading}
                                            onChange={(e, newValue) =>  {
                                                onChange(newValue);

                                                if (newValue && newValue.length > 0) {
                                                    setInpSelectAllDisabled(true);
                                                    setInpPaygroupDisabled(true);
                                                    setInpSchemeDisabled(true);
                                                } else {
                                                    setInpSelectAllDisabled(false)
                                                    setInpPaygroupDisabled(false);
                                                    setInpSchemeDisabled(false);
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField 
                                                    {...params} 
                                                    label="Select Pensioners" 
                                                    placeholder="Select a Pensioner ..." 
                                                    error={!!error}
                                                    helperText={error ? error.message : ''}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                    control={control}
                                    name='pensionersList'
                                />
                            </Grid>
                            
                            {/* Pay Group List */}
                            <Grid item xs={12} >
                                <Controller
                                    render={({ field: { onChange, value, name }, fieldState: { error }}) => (
                                        <Autocomplete
                                            multiple
                                            id="paygroup-list"
                                            limitTags={2}
                                            disabled={inpPaygroupDisabled || formLoading}
                                            options={transformPaygroups(payGroupList)}
                                            disableCloseOnSelect
                                            filterSelectedOptions
                                            isOptionEqualToValue={(option, value) => option.value == value.value}
                                            loading={loading}
                                            onChange={(e, newValue) =>  {
                                                onChange(newValue);

                                                if (newValue && newValue.length > 0) {
                                                    setInpSelectAllDisabled(true);
                                                    setInpPensionersDisabled(true);
                                                    setInpSchemeDisabled(true);
                                                } else {
                                                    setInpSelectAllDisabled(false)
                                                    setInpPensionersDisabled(false);
                                                    setInpSchemeDisabled(false);
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Pay Groups"
                                                    placeholder="Select a Pay Group"
                                                    error={!!error}
                                                    helperText={error ? error.message : ''}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                    control={control}
                                    name="paygroupList"
                                />
                            </Grid>
                            
                            {/* Scheme List */}
                            <Grid item xs={12} >
                                <Controller
                                    render={({ field: { onChange, value, name }, fieldState: { error }}) => (
                                        
                                        <Autocomplete
                                            multiple
                                            id="scheme-list"
                                            limitTags={2}
                                            disabled={inpSchemeDisabled || formLoading}
                                            options={transformSchemes(pensionSchemeList)}
                                            disableCloseOnSelect
                                            getOptionLabel={(option) => option.label + ' (' + option.value + ')' }
                                            filterSelectedOptions
                                            isOptionEqualToValue={(option, value) => option.value == value.value}
                                            loading={loading}
                                            onChange={(e, newValue) =>  {
                                                onChange(newValue);

                                                if (newValue && newValue.length > 0) {
                                                    setInpSelectAllDisabled(true);
                                                    setInpPensionersDisabled(true);
                                                    setInpPaygroupDisabled(true);
                                                } else {
                                                    setInpSelectAllDisabled(false)
                                                    setInpPensionersDisabled(false);
                                                    setInpPaygroupDisabled(false);
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Schemes"
                                                    placeholder="Select a Scheme"
                                                    sx={{ mb: 2 }}
                                                    error={!!error}
                                                    helperText={error ? error.message : ''}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                            sx={{ width: '100%', justifyContent: 'center' }}
                                        />

                                    )}
                                    control={control}
                                    name="schemeList"
                                />
                            </Grid>
                        </Grid>

                    </form>
                </DialogContent>

                <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
                    <Button onClick={handleClose} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit(submitMessage)}
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={20} />}
                    >
                        Send Message
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
export default SendMessage;